import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react';
import {
  hasMeaningfulInviteFormData,
  loadInviteFormData,
  saveInviteFormData,
  type InviteFormState,
} from './invite-session-storage';

const FORM_SAVE_DEBOUNCE_MS = 300;

export type UseInviteFormPersistenceOptions = InviteFormState & {
  eventId: number;
  isClosingRef: RefObject<boolean>;
};

export const useInviteFormPersistence = ({
  eventId,
  isClosingRef,
  invitationExpiresAt,
  selectedInvitationChannels,
  invitationText,
  invitationSubject,
  inviteAcceptionInfo,
  isOutlookMeetingEnabled,
  outlookMeetingStartTime,
  outlookMeetingEndTime,
  outlookMeetingLocationOrLink,
  outlookMeetingComment,
  ticketDate,
  isSkipTicketSendChecked,
  isTicketSettingsCollapsed,
  selectedTicketChannels,
  ticketText,
  ticketSubject,
}: UseInviteFormPersistenceOptions): { hasRestoredFromSessionRef: MutableRefObject<boolean> } => {
  const hasRestoredFromSessionRef = useRef(hasMeaningfulInviteFormData(loadInviteFormData(eventId)));
  const skipNextSaveRef = useRef(hasRestoredFromSessionRef.current);
  const pendingFormStateRef = useRef<InviteFormState>({
    invitationExpiresAt,
    selectedInvitationChannels,
    invitationText,
    invitationSubject,
    inviteAcceptionInfo,
    isOutlookMeetingEnabled,
    outlookMeetingStartTime,
    outlookMeetingEndTime,
    outlookMeetingLocationOrLink,
    outlookMeetingComment,
    ticketDate,
    isSkipTicketSendChecked,
    isTicketSettingsCollapsed,
    selectedTicketChannels,
    ticketText,
    ticketSubject,
  });

  pendingFormStateRef.current = {
    invitationExpiresAt,
    selectedInvitationChannels,
    invitationText,
    invitationSubject,
    inviteAcceptionInfo,
    isOutlookMeetingEnabled,
    outlookMeetingStartTime,
    outlookMeetingEndTime,
    outlookMeetingLocationOrLink,
    outlookMeetingComment,
    ticketDate,
    isSkipTicketSendChecked,
    isTicketSettingsCollapsed,
    selectedTicketChannels,
    ticketText,
    ticketSubject,
  };

  useEffect(() => {
    const flushPendingSave = () => {
      if (isClosingRef.current) {
        return;
      }

      saveInviteFormData(eventId, pendingFormStateRef.current);
    };

    window.addEventListener('beforeunload', flushPendingSave);
    window.addEventListener('pagehide', flushPendingSave);

    return () => {
      window.removeEventListener('beforeunload', flushPendingSave);
      window.removeEventListener('pagehide', flushPendingSave);
    };
  }, [eventId, isClosingRef]);

  useEffect(() => {
    if (isClosingRef.current) {
      return;
    }

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (isClosingRef.current) {
        return;
      }

      saveInviteFormData(eventId, pendingFormStateRef.current);
    }, FORM_SAVE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [
    eventId,
    invitationExpiresAt,
    selectedInvitationChannels,
    invitationText,
    invitationSubject,
    inviteAcceptionInfo,
    isOutlookMeetingEnabled,
    outlookMeetingStartTime,
    outlookMeetingEndTime,
    outlookMeetingLocationOrLink,
    outlookMeetingComment,
    ticketDate,
    isSkipTicketSendChecked,
    isTicketSettingsCollapsed,
    selectedTicketChannels,
    ticketText,
    ticketSubject,
    isClosingRef,
  ]);

  return { hasRestoredFromSessionRef };
};

export const getInitialInviteFormState = (eventId: number): Partial<InviteFormState> =>
  loadInviteFormData(eventId) ?? {};

export type { InviteFormState };
